import {
  Component,
  OnDestroy
} from '@angular/core';
import * as fromRoot from 'src/store/index';
import * as fromWorkspace from 'src/store/workspace';
import {
  Store
} from '@ngrx/store';
import {
  Observable,
  Subscription,
  empty
} from 'rxjs';
import {
  PayPeriod,
  SearchByOptions,
  ClientDTO,
  StaffDTO,
  DateRangeOutput,
  SearchDateOptions,
  SplitSearchButtonItem,
  UserRole,
  View,
  Category
} from 'src/store/workspace/workspace.model';
import {
  DropDownFilterSettings
} from '@progress/kendo-angular-dropdowns';
import * as moment from 'moment';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-searchbar',
  templateUrl: './searchbar.component.html',
  styleUrls: ['./searchbar.component.scss']
})
export class SearchbarComponent implements OnDestroy {
  payPeriods$: Observable < PayPeriod[] > ;
  payPeriodsSubs: Subscription;
  payPeriods: PayPeriod[];
  view$: Observable < View > ;
  view: View;
  View = View;
  viewSubs: Subscription;
  searchByOptions$: Observable < SearchByOptions > ;
  clients$: Observable < ClientDTO[] > ;
  clientSearch$: Observable<ClientDTO>;
  categories$: Observable <Category[]>;
  staff$: Observable < StaffDTO > ;
  staffSearch$: Observable < StaffDTO > ;
  staffList$: Observable < StaffDTO[] > ;
  searchByOptionSubs: Subscription;
  searchByOptions: SearchByOptions;
  SearchDateOptions = fromWorkspace.SearchDateOptions;
  filterSettings: DropDownFilterSettings;
  UserRole = UserRole;
  newUiUrl = environment.newUiUrl;
  selectedSearch: SplitSearchButtonItem;
  btnSearchData: Array < SplitSearchButtonItem > = [{
      text: 'Search Detail',
      icon: 'search',
      view: View.detail,
      isVisible: () => true,
      click: () => {
        if (this.view === View.descriptions || this.view === View.categories) {
          this.resetFilters();
        }
        this.selectedSearch = this.btnSearchData[0];
        this.search();
      }
    }, {
      text: 'Search Summary',
      icon: 'search',
      view: View.summary,
      isVisible: () => true,
      click: () => {
        if (this.view === View.descriptions || this.view === View.categories) {
          this.resetFilters();
        }
        this.selectedSearch = this.btnSearchData[1];
        this.search();
      }
    },
    {
      text: 'Search Categories',
      icon: 'categorize',
      view: View.categories,
      isVisible: (staff: StaffDTO) => staff.isCurrentUserAdmin,
      click: () => {
        if (this.view === View.detail || this.view === View.summary) {
          this.resetFilters();
        }
        this.selectedSearch = this.btnSearchData[2];
        this.store.dispatch(new fromWorkspace.SetView(this.selectedSearch.view));
      }
    },
    {
      text: 'Search Descriptions',
      icon: 'list-bulleted',
      view: View.descriptions,
      isVisible: (staff: StaffDTO) => staff.isCurrentUserAdmin,
      click: () => {
        if (this.view === View.detail || this.view === View.summary) {
          this.resetFilters();
        }
        this.selectedSearch = this.btnSearchData[3];
        this.store.dispatch(new fromWorkspace.SetView(this.selectedSearch.view));
      }
    },
    {
      text: 'Staff',
      icon: 'list-bulleted',
      view: View.staff,
      isVisible: (staff: StaffDTO) => staff.isCurrentUserAdmin,
      click: () => {
        if (this.view === View.detail || this.view === View.summary) {
          this.resetFilters();
        }
        this.selectedSearch = this.btnSearchData[4];
        this.store.dispatch(new fromWorkspace.SetView(this.selectedSearch.view));
      }
    },
    {
      text: 'Clients',
      icon: 'list-bulleted',
      view: View.clients,
      isVisible: (staff: StaffDTO) => staff.isCurrentUserAdmin,
      click: () => {
        if (this.view === View.detail || this.view === View.summary) {
          this.resetFilters();
        }
        this.selectedSearch = this.btnSearchData[5];
        this.store.dispatch(new fromWorkspace.SetView(this.selectedSearch.view));
      }
    }
  ];

  constructor(private store: Store < fromRoot.AppState > ) {
    this.selectedSearch = this.btnSearchData[0];

    this.staff$ = this.store.select(state => state.workspace.staff);
    this.staffList$ = this.store.select(state => state.workspace.staffList);
    this.staffSearch$ = this.store.select(state => state.workspace.searchByOptions.staff);

    this.view$ = this.store.select(state => state.workspace.view);
    this.viewSubs = this.view$.subscribe(view => this.view = view);

    this.payPeriods$ = this.store.select(state => Object.values(state.workspace.payPeriods));
    this.payPeriodsSubs = this.payPeriods$.subscribe(pps => this.payPeriods = pps);

    this.searchByOptions$ = this.store.select(state => state.workspace.searchByOptions);

    this.clients$ = this.store.select(state => {
      return Object.values(state.workspace.clients).sort((a, b) => {
        if (a.clientName.toLowerCase() > b.clientName.toLowerCase()) {
          return 1;
        }
        if (a.clientName.toLowerCase() < b.clientName.toLowerCase()) {
          return -1;
        }
        return 0;
      });
    });
    this.clientSearch$ = this.store.select(state => state.workspace.searchByOptions.client);

    this.categories$ = this.store.select(state => {
      let categories = Object.values(state.workspace.categories);
      if (state.workspace.searchByOptions.client && state.workspace.searchByOptions.client.clientID) {
        categories = categories.filter(cat => cat.clientID === state.workspace.searchByOptions.client.clientID);
      }
      return categories;
    });

    this.searchByOptionSubs = this.searchByOptions$.subscribe(option => {
      this.searchByOptions = option;
      this.btnSearchData.find(item => item.text === 'Search Detail').disabled = false;
      this.btnSearchData.find(item => item.text === 'Search Summary').disabled = false;
      if (option.dateOption === SearchDateOptions.DateRange) {
        this.btnSearchData.find(item => item.text === 'Search Detail').disabled = false;
        this.btnSearchData.find(item => item.text === 'Search Summary').disabled = true;
        if (!option.dateRange || !option.dateRange.DateStarting || !option.dateRange.DateEnding) {
          this.btnSearchData.find(item => item.text === 'Search Detail').disabled = true;
        }
      }
    });
    this.filterSettings = {
      operator: 'contains',
      caseSensitive: false
    };
  }

  resetFilters = () => {
    this.store.dispatch(new fromWorkspace.SelectClient(null));
  }

  getBtnSearchData = (staff: StaffDTO) => {
    if (staff) {
      return this.btnSearchData.filter(btn => btn.isVisible(staff));
    }
  }

  staffSelected = (staff: StaffDTO) => {
    this.store.dispatch(new fromWorkspace.SelectStaffForSearch(staff));
    this.store.dispatch(new fromWorkspace.SetSelectedStaffID(staff.staffID));
    this.store.dispatch(new fromWorkspace.LoadPTO());
    this.store.dispatch(new fromWorkspace.Search());
  }

  getDefaultStaffIndex = (staff: StaffDTO, list: StaffDTO[]) => {
    return list.findIndex(s => s.staffID === staff.staffID);
  }

  
  viewHasDateSearch() {
    return this.view === View.detail || this.view === View.summary;
  }

  viewHasClientSearch() {
    return this.view !== View.staff;
  }

  viewHasStaffSearch() {
    return this.view === View.detail || this.view === View.summary || this.view === View.staff;
  }

  viewHasCategorySearch() {
    return this.view === View.descriptions;
  }

  search = () => {
    this.store.dispatch(new fromWorkspace.SetView(this.selectedSearch.view));
    this.store.dispatch(new fromWorkspace.Search());
  }

  ngOnDestroy = () => {
    this.searchByOptionSubs.unsubscribe();
    this.viewSubs.unsubscribe();
  }

  setDateOption = (option: fromWorkspace.SearchDateOptions) => {
    this.selectedSearch = this.btnSearchData[0];
    this.store.dispatch(new fromWorkspace.SelectDateRange({
      type: 'all',
      value: null
    }));
    this.store.dispatch(new fromWorkspace.SetSearchByDateOption(option));
  }

  isSelectedDateOption = (option: fromWorkspace.SearchDateOptions): boolean => {
    return this.searchByOptions.dateOption === option;
  }

  payPeriodSelected = (range: {
    start: Date,
    end: Date
  }) => {
    const start = moment(range.start);
    const p = this.payPeriods.find(item => {
      const pStart = moment(item.payPeriodStarting);
      if (pStart.isSame(start, 'd')) {
        return item;
      }
    });
    this.store.dispatch(new fromWorkspace.SelectPayPeriod(p));
  }

  clientSelected = (c: ClientDTO) => {
    this.store.dispatch(new fromWorkspace.SelectClient(c));
    this.store.dispatch(new fromWorkspace.SelectCategoryForSearch(null));
    this.store.dispatch(new fromWorkspace.Search());
  }

  categorySelected = (cat: Category) => {
    this.store.dispatch(new fromWorkspace.SelectCategoryForSearch(cat));
  }

  dateRangeSelected = (input: DateRangeOutput) => {
    this.store.dispatch(new fromWorkspace.SelectDateRange(input));
  }

}
